import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, X, ShieldCheck, Send, Activity, Loader2 } from "lucide-react";
import { appsApi, errMsg, notifyError, inputCls, fmtDate } from "@/lib/appsApi";
import { AccessLog } from "@/components/apps/AccessLog";
import { TabChips, TesterTabs } from "@/components/apps/TesterTabs";

const TesterRow = ({ u, me, tabs, onChange, onRemove }) => {
  const [sending, setSending] = useState(false);
  const [open, setOpen] = useState(false);

  const invite = async () => {
    setSending(true);
    try {
      const r = await appsApi.post(`/admin/users/${u.user_id}/invite`);
      onChange({ ...u, ...(r?.data ?? {}) });
      toast.success(`Invite emailed to ${u.email}`);
    } catch (err) {
      notifyError(errMsg(err, `Sending the invite to ${u.email}`));
    } finally {
      setSending(false);
    }
  };

  return (
    <li className="py-3" data-testid={`apps-tester-${u.user_id}`}>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
        <div className="min-w-0">
          <p className="break-words text-sm font-medium text-ink">{u.email}</p>
          <p className="text-xs text-slate-500">{u.name || "Not signed in yet"}{u.has_password ? " · password set" : ""}</p>
          <p className="mt-1 text-xs text-slate-600" data-testid={`apps-tester-activity-${u.user_id}`}>
            {u.invited_at ? `Invited ${fmtDate(u.invited_at)}` : "Not invited"}
            {u.last_login_at ? ` · Last sign-in ${fmtDate(u.last_login_at)}` : ""}
            {` · ${u.download_count ?? 0} download${u.download_count === 1 ? "" : "s"}`}
            {u.last_download_at ? ` (last ${fmtDate(u.last_download_at)})` : ""}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {u.role === "admin" && <span className="mr-1 inline-flex items-center gap-1 rounded-full bg-ink px-2.5 py-1 text-[11px] text-white"><ShieldCheck className="h-3 w-3" />Admin</span>}
          <button onClick={invite} disabled={sending} title={u.invited_at ? "Resend invite" : "Send invite"} className="inline-flex items-center gap-1 rounded-full px-2.5 py-1.5 text-xs font-medium text-sky-700 hover:bg-sky-50 disabled:opacity-50" data-testid={`apps-invite-tester-${u.user_id}`}>
            {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}{u.invited_at ? "Resend" : "Invite"}
          </button>
          <button onClick={() => setOpen(!open)} aria-label="Show downloads" aria-pressed={open} className={`rounded-full p-2 ${open ? "bg-sky-50 text-sky-700" : "text-slate-500 hover:bg-sky-50 hover:text-sky-700"}`} data-testid={`apps-tester-log-toggle-${u.user_id}`}><Activity className="h-4 w-4" /></button>
          {u.user_id !== me.user_id && (
            <button onClick={() => onRemove(u)} aria-label={`Remove ${u.email}`} className="rounded-full p-2 text-slate-500 hover:bg-red-50 hover:text-red-600" data-testid={`apps-remove-tester-${u.user_id}`}><X className="h-4 w-4" /></button>
          )}
        </div>
      </div>
      <TesterTabs u={u} tabs={tabs} onChange={onChange} />
      {open && <AccessLog params={{ user_id: u.user_id }} show="file" testid={`apps-tester-log-${u.user_id}`} />}
    </li>
  );
};

export const TesterManager = ({ me, tabs = [] }) => {
  const [users, setUsers] = useState([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("tester");
  const [sendInvite, setSendInvite] = useState(true);
  const [newTabs, setNewTabs] = useState(null);
  const [busy, setBusy] = useState(false);
  const chosen = newTabs ?? tabs.map((t) => t.id);

  useEffect(() => {
    appsApi.get("/admin/users").then((r) => setUsers(Array.isArray(r?.data) ? r.data : [])).catch((err) => notifyError(errMsg(err, "Loading the tester list")));
  }, []);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await appsApi.post("/admin/users", { email, role, send_invite: sendInvite, tabs: chosen });
      setUsers((u) => [...u, r.data]);
      setEmail("");
      setNewTabs(null);
      if (r?.data?.invite_error) notifyError(r.data.invite_error);
      else toast.success(sendInvite ? `Added and invite emailed to ${r.data.email}` : "Added — use Invite to email them a link");
    } catch (err) {
      notifyError(errMsg(err, "Adding the tester"));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (u) => {
    try {
      await appsApi.delete(`/admin/users/${u.user_id}`);
      setUsers((list) => list.filter((x) => x.user_id !== u.user_id));
      toast.success(`${u.email} removed`);
    } catch (err) {
      notifyError(errMsg(err, `Removing ${u.email}`));
    }
  };

  return (
    <section className="rounded-[28px] border border-sky-100 bg-white p-6 sm:p-8" data-testid="apps-tester-manager">
      <h3 className="font-display text-xl font-semibold text-ink">Testers</h3>
      <form onSubmit={add} className="mt-5" data-testid="apps-add-tester-form">
        <div className="flex flex-col gap-3 sm:flex-row">
          <input type="email" required placeholder="tester@email.com" value={email} onChange={(e) => setEmail(e.target.value)} className={`${inputCls} sm:flex-1`} data-testid="apps-add-tester-email" />
          <select value={role} onChange={(e) => setRole(e.target.value)} className={`${inputCls} sm:w-32`} data-testid="apps-add-tester-role">
            <option value="tester">Tester</option>
            <option value="admin">Admin</option>
          </select>
          <button type="submit" disabled={busy} className="inline-flex items-center justify-center gap-2 rounded-full bg-sky-600 px-5 py-3.5 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-60" data-testid="apps-add-tester-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} Add
          </button>
        </div>
        {role === "tester" && tabs.length > 0 && (
          <div className="mt-3">
            <p className="mb-1.5 text-xs text-slate-600">Tabs this tester can see</p>
            <TabChips tabs={tabs} selected={chosen} onToggle={(id) => setNewTabs(chosen.includes(id) ? chosen.filter((x) => x !== id) : [...chosen, id])} testid="apps-add-tester-tabs" />
          </div>
        )}
        <label className="mt-3 inline-flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={sendInvite} onChange={(e) => setSendInvite(e.target.checked)} className="h-4 w-4 accent-sky-600" data-testid="apps-add-tester-invite" />
          Email them an invite with a link to this page
        </label>
      </form>
      <ul className="mt-6 divide-y divide-slate-100" data-testid="apps-tester-list">
        {users.map((u) => (
          <TesterRow key={u.user_id} u={u} me={me} tabs={tabs} onRemove={remove} onChange={(nu) => setUsers((l) => l.map((x) => (x.user_id === nu.user_id ? { ...x, ...nu } : x)))} />
        ))}
      </ul>
    </section>
  );
};
